import { ArrowRight, BookOpen, Compass, DollarSign, Globe, Hammer, type LucideIcon, Users } from "lucide-react";
import { useTranslation } from "react-i18next";
import { AnimatedSection } from "@/src/common/components/animated-section";
import { CtaLink } from "@/src/common/components/cta-link";
import { EyebrowBadge } from "@/src/common/components/eyebrow-badge";
import { PageHelmet } from "@/src/common/components/page-helmet";
import { Reveal } from "@/src/common/components/reveal";
import { SectionHeading } from "@/src/common/components/section-heading";
import { SITE_ROUTES } from "@/src/common/constants/site-routes";

interface PerkEntry {
	id: "remote" | "equity" | "learning" | "async";
	Icon: LucideIcon;
}

interface CultureEntry {
	id: "async" | "ownership" | "craft" | "learning";
	Icon: LucideIcon;
}

const PERKS: ReadonlyArray<PerkEntry> = [
	{ id: "remote", Icon: Globe },
	{ id: "equity", Icon: DollarSign },
	{ id: "learning", Icon: BookOpen },
	{ id: "async", Icon: Compass },
];

const CULTURE: ReadonlyArray<CultureEntry> = [
	{ id: "async", Icon: Compass },
	{ id: "ownership", Icon: Users },
	{ id: "craft", Icon: Hammer },
	{ id: "learning", Icon: BookOpen },
];

export function CareersPage() {
	const { t } = useTranslation("careers");

	return (
		<>
			<PageHelmet title={t("meta.title")} description={t("meta.description")} />

			<section className="bg-mesh relative isolate overflow-hidden pt-20 pb-16 md:pt-32 md:pb-20">
				<div className="mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
					<SectionHeading
						as="h1"
						eyebrow={<EyebrowBadge tone="ink">{t("hero.eyebrow")}</EyebrowBadge>}
						title={t("hero.title")}
						titleAccent={t("hero.title_accent")}
						description={t("hero.subtitle")}
					/>
				</div>
			</section>

			<AnimatedSection className="pb-12 md:pb-20">
				<div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
					<div className="grid gap-3 rounded-3xl border border-default-200 bg-white p-4 shadow-card md:grid-cols-4 md:gap-0 md:p-2">
						{PERKS.map(({ id, Icon }, i) => (
							<div key={id} className={`flex items-center gap-3 px-4 py-3 ${i > 0 ? "md:border-l md:border-default-200/60" : ""}`}>
								<span className="inline-flex size-10 shrink-0 items-center justify-center rounded-xl bg-brand-50 text-brand-700">
									<Icon className="h-5 w-5" />
								</span>
								<div className="min-w-0">
									<p className="truncate text-sm font-semibold text-foreground">{t(`perks.${id}.title`)}</p>
									<p className="truncate text-xs text-default-500">{t(`perks.${id}.body`)}</p>
								</div>
							</div>
						))}
					</div>
				</div>
			</AnimatedSection>

			<section className="py-20 md:py-24">
				<div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
					<SectionHeading title={t("culture.title")} titleAccent={t("culture.title_accent")} />
					<div className="mt-16 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
						{CULTURE.map(({ id, Icon }, index) => (
							<Reveal key={id} delay={index * 0.08}>
								<article className="flex h-full flex-col gap-4 rounded-3xl border border-default-200 bg-white p-7 shadow-card hover-lift">
									<span className="inline-flex size-11 items-center justify-center rounded-2xl bg-brand-50 text-brand-700">
										<Icon className="h-5 w-5" />
									</span>
									<h3 className="text-lg font-bold text-foreground">{t(`culture.items.${id}.title`)}</h3>
									<p className="text-sm leading-relaxed text-default-600">{t(`culture.items.${id}.description`)}</p>
								</article>
							</Reveal>
						))}
					</div>
				</div>
			</section>

			<AnimatedSection className="border-t border-default-200/60 bg-content1/40 pb-32 pt-24 md:pt-32">
				<div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
					<h2 className="text-center text-balance text-3xl font-bold tracking-tight text-foreground md:text-4xl">{t("openings.title")}</h2>
					<Reveal delay={0.1} className="mt-12 rounded-3xl border-2 border-dashed border-default-300 bg-white/60 p-12 text-center md:p-16">
						<h3 className="text-2xl font-bold text-foreground">{t("openings.empty_title")}</h3>
						<p className="mx-auto mt-4 max-w-xl text-default-600">{t("openings.empty_subtitle")}</p>
						<div className="mt-8 flex justify-center">
							<CtaLink to={SITE_ROUTES.CONTACT} variant="primary" size="lg" endContent={<ArrowRight className="h-4 w-4" />}>
								{t("openings.empty_cta")}
							</CtaLink>
						</div>
					</Reveal>
				</div>
			</AnimatedSection>
		</>
	);
}
